// Onboarding stages as defined in the scoping document
// These codes are the single source of truth for prospect journey state
// Updated: Two-meeting flow - intro meeting first, then optional personalised demo
// Updated: Jan 7, 2026 - Added PROVISIONED stage for post-provisioning

export const STAGES = {
  USP_REVIEW: '01_USP_REVIEW',           // Legacy - public landing page
  INTEREST_CONFIRMED: '02_INTEREST_CONFIRMED', // NEW USER START - book intro meeting
  GENERIC_DEMO: '03_GENERIC_DEMO',       // Intro meeting scheduled
  CONSENT_INITIATE: '03B_CONSENT_INITIATE', // Personalised path - admin initiated
  CONSENT_PENDING: '04_CONSENT_PENDING',
  CONSENT_SIGNED: '05_CONSENT_SIGNED',
  API_SETUP: '06_API_SETUP',
  API_CONNECTED: '07_API_CONNECTED',     // Book personalised demo meeting
  DEMO_SCHEDULED: '10_DEMO_SCHEDULED',   // Personalised demo meeting scheduled
  PROVISIONED: '11_PROVISIONED',         // NEW: User provisioned in FleetVision
  ABANDONED: '99_ABANDONED'
}

export const STAGE_INFO = {
  [STAGES.USP_REVIEW]: {
    code: '01_USP_REVIEW',
    name: 'USP Review',
    description: 'Legacy - public landing page',
    path: '/onboarding/interest'
  },
  [STAGES.INTEREST_CONFIRMED]: {
    code: '02_INTEREST_CONFIRMED',
    name: 'Book Intro Call',
    description: 'Prospect books intro meeting with Robert',
    path: '/onboarding/schedule-intro'
  },
  [STAGES.GENERIC_DEMO]: {
    code: '03_GENERIC_DEMO',
    name: 'Intro Scheduled',
    description: 'Intro meeting scheduled - awaiting call',
    path: '/onboarding/schedule-intro'
  },
  [STAGES.CONSENT_INITIATE]: {
    code: '03B_CONSENT_INITIATE',
    name: 'Consent Initiate',
    description: 'Prospect filling in signer details for agreement',
    path: '/onboarding/consent'
  },
  [STAGES.CONSENT_PENDING]: {
    code: '04_CONSENT_PENDING',
    name: 'Consent Pending',
    description: 'Awaiting agreement signature',
    path: '/onboarding/consent-pending'
  },
  [STAGES.CONSENT_SIGNED]: {
    code: '05_CONSENT_SIGNED',
    name: 'Consent Signed',
    description: 'DocuSign agreement completed',
    path: '/onboarding/consent-complete'
  },
  [STAGES.API_SETUP]: {
    code: '06_API_SETUP',
    name: 'API Setup',
    description: 'Prospect configuring dashcam provider API',
    path: '/onboarding/api-setup'
  },
  [STAGES.API_CONNECTED]: {
    code: '07_API_CONNECTED',
    name: 'Book Demo',
    description: 'Provider connected - book personalised demo walkthrough',
    path: '/onboarding/schedule-demo'
  },
  [STAGES.DEMO_SCHEDULED]: {
    code: '10_DEMO_SCHEDULED',
    name: 'Demo Scheduled',
    description: 'Personalised demo meeting scheduled with Robert',
    path: '/onboarding/schedule-demo'
  },
  [STAGES.PROVISIONED]: {
    code: '11_PROVISIONED',
    name: 'Provisioned',
    description: 'User provisioned in FleetVision - onboarding complete',
    path: '/onboarding/complete'
  },
  [STAGES.ABANDONED]: {
    code: '99_ABANDONED',
    name: 'Abandoned',
    description: 'Prospect inactive for defined period',
    path: null
  }
}

// Stage transition rules
// Updated for two-meeting flow
// Updated Jan 7, 2026: Added PROVISIONED transitions
export const STAGE_TRANSITIONS = {
  [STAGES.USP_REVIEW]: [STAGES.INTEREST_CONFIRMED],
  [STAGES.INTEREST_CONFIRMED]: [STAGES.GENERIC_DEMO, STAGES.CONSENT_INITIATE, STAGES.ABANDONED],
  [STAGES.GENERIC_DEMO]: [STAGES.CONSENT_INITIATE, STAGES.INTEREST_CONFIRMED, STAGES.ABANDONED],
  [STAGES.CONSENT_INITIATE]: [STAGES.CONSENT_PENDING, STAGES.ABANDONED],
  [STAGES.CONSENT_PENDING]: [STAGES.CONSENT_SIGNED, STAGES.ABANDONED],
  [STAGES.CONSENT_SIGNED]: [STAGES.API_SETUP],
  [STAGES.API_SETUP]: [STAGES.API_CONNECTED, STAGES.ABANDONED],
  [STAGES.API_CONNECTED]: [STAGES.DEMO_SCHEDULED, STAGES.ABANDONED],
  [STAGES.DEMO_SCHEDULED]: [STAGES.API_CONNECTED, STAGES.PROVISIONED, STAGES.ABANDONED],
  [STAGES.PROVISIONED]: [STAGES.ABANDONED], // Terminal state, can only be abandoned
  [STAGES.ABANDONED]: [] // Terminal state
}

// Check if transition is valid
export const canTransitionTo = (fromStage, toStage) => {
  const allowedTransitions = STAGE_TRANSITIONS[fromStage] || []
  return allowedTransitions.includes(toStage)
}

// Get route path for a stage
export const getRoutePath = (stage) => {
  const info = STAGE_INFO[stage]
  return info?.path || null
}

// Get stage info by code (e.g., '02_INTEREST_CONFIRMED')
export const getStageByCode = (code) => {
  for (const [key, info] of Object.entries(STAGE_INFO)) {
    if (info.code === code) {
      return { key, ...info }
    }
  }
  return null
}
